<template>
    <c-file-input
        :value="value"
        :multiple="false"
        :display-preview="false"
        :accept="accept"
        :max-mb-size="maxMbSize"
        :disabled="disabled"
        @input="emit('input', $event)"
    >
        <template #uploadTrigger="{ onFileUpload, disabled: triggerDisabled }">
            <div class="framed-row upload-row w-100" @click="!triggerDisabled && onFileUpload()">
                <span class="upload-thumbnail">
                    <img v-if="imageUrl" :src="imageUrl" alt="" />
                    <font-awesome-icon v-else :icon="['far', 'image']" />
                </span>
                <div class="upload-text">
                    <strong>{{ name || placeholder }}</strong>
                    <small class="text-muted">{{ hint || defaultHint }}</small>
                </div>
                <c-button
                    v-if="removable"
                    size="icon"
                    :icon="['far', 'trash-can']"
                    :disabled="triggerDisabled"
                    @click.stop="emit('remove')"
                />
                <c-button
                    size="icon"
                    :icon="['far', 'arrow-up-from-bracket']"
                    :disabled="triggerDisabled"
                    @click.stop="onFileUpload"
                />
            </div>
        </template>
    </c-file-input>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import CButton from '@/components/shared/CButton';
import CFileInput from '@/components/shared/CFileInput';

const props = withDefaults(
    defineProps<{
        /** Files currently held by the input; keep it empty when the upload is persisted elsewhere. */
        value?: File[];
        /** Thumbnail of the already uploaded image. */
        imageUrl?: string;
        /** Name of the uploaded file, replaces the placeholder once something is selected. */
        name?: string;
        placeholder?: string;
        /** Overrides the "PNG, JPG - Max 10MB" line derived from accept/maxMbSize. */
        hint?: string;
        accept?: string;
        maxMbSize?: number;
        disabled?: boolean;
        removable?: boolean;
    }>(),
    {
        value: () => [],
        placeholder: 'Upload Image',
        accept: '.png,.jpg,.jpeg,.webp',
        maxMbSize: 10
    }
);

const emit = defineEmits<{
    (event: 'input', value: File[]): void;
    (event: 'remove'): void;
}>();

const defaultHint = computed(() => {
    const extensions = props.accept
        .split(',')
        .map((extension) => extension.trim().replace(/^\./, '').toUpperCase())
        // jpg/jpeg is the same format for the user, only the shorter one is worth showing
        .filter((extension) => extension !== 'JPEG');

    return `${extensions.join(', ')} - Max ${props.maxMbSize}MB`;
});
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.framed-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid get-color('gray-light');
    border-radius: 0.25rem;
    padding: 0.625rem;
    cursor: pointer;
}

.upload-row {
    .upload-thumbnail {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 2.75rem;
        height: 2.75rem;
        border: 1px solid get-color('gray-light');
        border-radius: 0.25rem;
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .upload-text {
        display: flex;
        flex-direction: column;
        line-height: 1.25;
        margin-right: auto;
    }
}
</style>
